/**
 * Segment the pipe surface patch connected to the seed click, by **normal
 * smoothness region growing**.
 *
 * Why this exists: estimating the axis from *every* point in the seed box is
 * fragile — a box around a small pipe is dominated by ground / wall points
 * whose normals all point the same way, which drags the normal-covariance
 * axis estimate to something perpendicular to the real pipe. Region growing
 * from the click follows the smoothly-curving pipe surface and stops at the
 * sharp normal discontinuity where the pipe meets the ground, isolating just
 * the pipe points before any axis fit.
 *
 * Normals are computed lazily (only for points the grow actually visits), so
 * this is cheap even when the seed box holds tens of thousands of points.
 */

import { addOuter, eigenSym3, normalize, zeroSym3 } from './linalg';
import { GridIndex } from './spatialIndex';
import type { Vec3 } from './types';

export interface SeedRegionResult {
  /** Indices of points on the segmented pipe surface patch. */
  indices: number[];
  /** Unit axis direction from the region's normal covariance. */
  axis: Vec3;
  axisConfidence: number;
  /** Index of the point nearest the click (region anchor). */
  seedIdx: number;
}

export function segmentSeedRegion(
  positions: Float64Array,
  count: number,
  index: GridIndex,
  seed: Vec3,
  neighRadius: number,
  opts: { angleThreshDeg?: number; maxRegion?: number; minNeighbours?: number; snapRadius?: number } = {},
): SeedRegionResult | null {
  const angleThreshDeg = opts.angleThreshDeg ?? 22;
  // How far from the click to hunt for the dense pipe surface when the click
  // landed on sparse noise/vegetation floating above the pipe.
  const snapRadius = opts.snapRadius ?? 0.18;
  // A few thousand connected surface points is ample for axis + radius; capping
  // here also bounds segmentation cost and keeps the cross-section fit local
  // (so a gently-bending pipe doesn't bias the radius).
  const maxRegion = opts.maxRegion ?? 8_000;
  const minNeighbours = opts.minNeighbours ?? 6;
  const cosThresh = Math.cos((angleThreshDeg * Math.PI) / 180);

  const normalCache = new Float64Array(count * 3);
  const normalState = new Int8Array(count); // 0 unknown, 1 valid, -1 invalid
  const neigh: number[] = [];

  const normalAt = (i: number): Vec3 | null => {
    if (normalState[i] === 1) return [normalCache[3 * i], normalCache[3 * i + 1], normalCache[3 * i + 2]];
    if (normalState[i] === -1) return null;
    const px = positions[3 * i];
    const py = positions[3 * i + 1];
    const pz = positions[3 * i + 2];
    const k = index.queryRadius(px, py, pz, neighRadius, neigh);
    if (k < minNeighbours) {
      normalState[i] = -1;
      return null;
    }
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let n = 0; n < k; n++) {
      const j = neigh[n];
      cx += positions[3 * j];
      cy += positions[3 * j + 1];
      cz += positions[3 * j + 2];
    }
    cx /= k;
    cy /= k;
    cz /= k;
    const cov = zeroSym3();
    for (let n = 0; n < k; n++) {
      const j = neigh[n];
      const dx = positions[3 * j] - cx;
      const dy = positions[3 * j + 1] - cy;
      const dz = positions[3 * j + 2] - cz;
      cov.xx += dx * dx;
      cov.yy += dy * dy;
      cov.zz += dz * dz;
      cov.xy += dx * dy;
      cov.xz += dx * dz;
      cov.yz += dy * dz;
    }
    const nrm = normalize(eigenSym3(cov).vectors[0]);
    if (nrm[0] === 0 && nrm[1] === 0 && nrm[2] === 0) {
      normalState[i] = -1;
      return null;
    }
    normalCache[3 * i] = nrm[0];
    normalCache[3 * i + 1] = nrm[1];
    normalCache[3 * i + 2] = nrm[2];
    normalState[i] = 1;
    return nrm;
  };

  // Anchor on the DENSEST surface point near the click, not merely the nearest.
  // A click that lands on sparse noise/vegetation floating above the pipe would
  // otherwise grow a phantom cylinder; the real pipe surface is dense, so the
  // local-density (neighbour count) maximum snaps us onto it.
  let seedIdx = -1;
  const cand: number[] = [];
  const tmp: number[] = [];
  const candCount = index.queryRadius(seed[0], seed[1], seed[2], snapRadius, cand);
  if (candCount > 0) {
    let bestDensity = -1;
    let bestScore = Infinity; // tie-break: closer to click wins
    const step = Math.max(1, Math.floor(candCount / 300));
    for (let c = 0; c < candCount; c += step) {
      const i = cand[c];
      const density = index.queryRadius(positions[3 * i], positions[3 * i + 1], positions[3 * i + 2], neighRadius, tmp);
      if (density < minNeighbours) continue;
      const dx = positions[3 * i] - seed[0];
      const dy = positions[3 * i + 1] - seed[1];
      const dz = positions[3 * i + 2] - seed[2];
      const distScore = dx * dx + dy * dy + dz * dz;
      // Prefer higher density; among comparable densities prefer the closer one.
      if (density > bestDensity || (density === bestDensity && distScore < bestScore)) {
        bestDensity = density;
        bestScore = distScore;
        seedIdx = i;
      }
    }
  }

  // Fallback: nearest point with a usable normal (e.g. very sparse cloud).
  if (seedIdx < 0 || !normalAt(seedIdx)) {
    let best = Infinity;
    let nearest = -1;
    for (let i = 0; i < count; i++) {
      const dx = positions[3 * i] - seed[0];
      const dy = positions[3 * i + 1] - seed[1];
      const dz = positions[3 * i + 2] - seed[2];
      const d = dx * dx + dy * dy + dz * dz;
      if (d < best) {
        best = d;
        nearest = i;
      }
    }
    if (nearest < 0) return null;
    seedIdx = nearest;
    if (!normalAt(seedIdx)) {
      const k = index.queryRadius(seed[0], seed[1], seed[2], neighRadius * 3, neigh);
      let found = -1;
      for (let n = 0; n < k; n++) {
        if (normalAt(neigh[n])) {
          found = neigh[n];
          break;
        }
      }
      if (found < 0) return null;
      seedIdx = found;
    }
  }

  // BFS region grow by local normal smoothness (abs dot — normal sign from PCA
  // is arbitrary so adjacent normals may be flipped).
  const visited = new Uint8Array(count);
  const region: number[] = [];
  const stack: number[] = [seedIdx];
  visited[seedIdx] = 1;
  while (stack.length > 0 && region.length < maxRegion) {
    const i = stack.pop()!;
    const ni = normalAt(i);
    if (!ni) continue;
    region.push(i);
    const k = index.queryRadius(positions[3 * i], positions[3 * i + 1], positions[3 * i + 2], neighRadius, neigh);
    for (let n = 0; n < k; n++) {
      const j = neigh[n];
      if (visited[j]) continue;
      const nj = normalAt(j);
      if (!nj) continue;
      const d = Math.abs(ni[0] * nj[0] + ni[1] * nj[1] + ni[2] * nj[2]);
      if (d >= cosThresh) {
        visited[j] = 1;
        stack.push(j);
      }
    }
  }

  // Axis = smallest-eigenvalue eigenvector of the region normal covariance.
  const M = zeroSym3();
  for (const i of region) {
    addOuter(M, [normalCache[3 * i], normalCache[3 * i + 1], normalCache[3 * i + 2]]);
  }
  const e = eigenSym3(M);
  const axis = normalize(e.vectors[0]);
  const [l0, l1, l2] = e.values;
  const denom = l2 > 1e-20 ? l2 : 1;
  const axisConfidence = Math.max(0, Math.min(1, (l1 - l0) / denom));

  return { indices: region, axis, axisConfidence, seedIdx };
}
